import { defineMessages, useIntl } from 'react-intl';
import classnames from 'classnames';

import { SlidingDirections } from '@jsmdg/yoshi';
import { PageType } from '../../../shared/enums/pageType';
import { type PageTile as TPageTile } from '../../../shared/types/pageContent';
import { type TrackingCreativePrefix } from '../../enums/trackingCreativePrefix';
import { CreativeName } from '../../enums/trackingPromotions';
import { trackSlideNavigationClick } from '../../helper/trackSlide';
import { LazySlider } from '../LazySlider';
import { PageSlide } from '../PageSlide';
import { pageTilesConfig } from './pageTilesConfig';
import styles from './PageTiles.module.scss';

type PageTilesProps = {
    readonly tiles: TPageTile[];
    readonly pageType: PageType;
    readonly isNextToNavigation: boolean;
    readonly identifier: string;
    readonly verticalPosition: number;
    readonly hasNavigationCreative: boolean;
    readonly trackingPrefix: TrackingCreativePrefix;
};

const messages = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const PageTiles = ({
    hasNavigationCreative,
    identifier,
    isNextToNavigation,
    pageType,
    tiles,
    trackingPrefix,
    verticalPosition,
}: PageTilesProps): JSX.Element | null => {
    const intl = useIntl();
    const sliderConfig = pageTilesConfig;

    if (tiles.length === 0 || !sliderConfig) {
        return null;
    }

    const slides = tiles.map((slide, index) => ({
        key: `${identifier}-${index}`,
        content: (
            <PageSlide
                linkUrl={slide.url}
                title={slide.title}
                image={slide.image}
                trackingName={slide.title?.toLowerCase().replace(' ', '-') || 'no id'}
                key={slide.title}
                position={`vertical${verticalPosition}_horizontal${index + 1}`}
                positionHorizontal={`${index + 1}`}
                positionVertical={`${
                    hasNavigationCreative ? verticalPosition - 1 : verticalPosition
                }`}
                pageType={pageType}
                trackingPrefix={trackingPrefix}
            />
        ),
    }));

    const handleSlideNext = (): void => {
        trackSlideNavigationClick(
            SlidingDirections.Next,
            verticalPosition.toString(),
            CreativeName.PageTile,
        );
    };

    const handleSlidePrevious = (): void => {
        trackSlideNavigationClick(
            SlidingDirections.Previous,
            verticalPosition.toString(),
            CreativeName.PageTile,
        );
    };

    return (
        <div
            className={classnames(styles.pageTiles, 'w-100 position-relative', {
                [styles.homePageNextToNavigation]: pageType === PageType.Home && isNextToNavigation,
                'mb-lg-1x': pageType === PageType.Home && isNextToNavigation,
            })}
        >
            <LazySlider
                a11yNextSlide={intl.formatMessage(messages.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messages.a11yPreviousSlide)}
                slides={slides}
                config={sliderConfig}
                classes={{
                    controlsContainer: 'mb-0 d-lg-none',
                }}
                handleShowNext={handleSlideNext}
                handleShowPrevious={handleSlidePrevious}
            />
        </div>
    );
};

export { PageTiles };
